import { DownloadRounded, DeleteRounded } from "@mui/icons-material";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ICellRendererParams } from "ag-grid-community";
import { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import CustomLoader from "../../../../../../../../../util/component/CustomLoader";
import { setSnackbar } from "../../../../../../../../../util/state/globalAction";
import { GlobalContext } from "../../../../../../../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../../../../../../../util/state/globalVariables";
import { downloadBookingAttachment, deleteBookingAttachment, useBookingAttachmentsQuery } from "../../../../../../../api/services";
import DialogCloseButton from "../../../../../../../components/DialogCloseButton";
import useDialog from "../../../../../../../hooks/useDialog";
import { BookingScope, ReactQueryKey, RouteParams } from "../../../../../../../types";
import { createFile, hasScope } from "../../../../../../../utils";
import { AttachmentsTableData } from "../TableAttachments";

export const AttachmentsActionsCell = (params: ICellRendererParams<AttachmentsTableData>) => {

    const { id: bookingId } = useParams<RouteParams>();

    const { dispatchGlobalData, globalData } = useContext(GlobalContext);

    const { t } = useTranslation('booking');

    const deleteDialog = useDialog();

    const queryClient = useQueryClient();

    const attachmentsQuery = useBookingAttachmentsQuery(
        bookingId,
        {
            refetchOnMount: false
        }
    );

    const downloadAttachmentMutation = useMutation(
        downloadBookingAttachment,
        {
            onSuccess: (data) => {
                createFile(data.file, data.fileName)
            },
            onError: () => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        message: t('message.error.default'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                })
            }
        }
    );
    const deleteAttachmentMutation = useMutation(
        deleteBookingAttachment,
        {
            onSuccess: () => {
                attachmentsQuery.refetch();
                queryClient.invalidateQueries({
                    queryKey: [ReactQueryKey.BookingById, bookingId]
                })
            },
            onError: () => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.default'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true
                        }
                    }
                })
            }
        }
    );

    const handleDownloadClick = useCallback(() => {
        if (!params.data) {
            return;
        }
        downloadAttachmentMutation.mutate(params.data.id);
    }, [params.data?.id]);
    const handleDeleteClick = useCallback(() => {
        deleteDialog.handleShow();
    }, []);
    const handleDeleteConfirm = useCallback(() => {
        if (!params.data) {
            return;
        }
        deleteAttachmentMutation.mutate(params.data.id);
    }, [params.data?.id]);

    return (
        <>
            {hasScope(globalData, BookingScope.DownloadAttachment) &&
                <IconButton
                    color="accent"
                    onClick={handleDownloadClick}
                    title={t('label.download')}
                >
                    <DownloadRounded />
                </IconButton>
            }
            {hasScope(globalData, BookingScope.DeleteAttachment) &&
                <IconButton
                    color="accent"
                    onClick={handleDeleteClick}
                    title={t('label.delete')}
                >
                    <DeleteRounded />
                </IconButton>
            }
            <Dialog open={deleteDialog.open} maxWidth="sm" fullWidth>
                <DialogCloseButton onClick={deleteDialog.handleClose} />
                <DialogTitle>
                    {t('label.deleteAttachment')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('message.info.delete_booking_attachment')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={deleteDialog.handleClose}>
                        {t('label.cancel')}
                    </Button>
                    <Button onClick={handleDeleteConfirm}>
                        {t('label.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
            <CustomLoader loading={
                downloadAttachmentMutation.isLoading ||
                deleteAttachmentMutation.isLoading
            } />
        </>
    );
};

export default AttachmentsActionsCell;
